import React, { useState, useEffect, useMemo } from 'react';
import axios from './lib/axiosClient';
import useAutoRefresh from './hooks/useAutoRefresh';

const DURATIONS = [15,30,45,60,90,120,150,180,240,300,360,480];
const fmtDur = (m) => { if (!m) return '—'; if (m < 60) return m + 'min'; var h = Math.floor(m/60); var r = m%60; return h + 'h' + (r ? ' ' + r + 'm' : ''); };
const fmtPrice = (p) => '$' + parseFloat(p || 0).toFixed(2);
const revenuePerHour = (s) => { var d = s.duration || 60; return ((parseFloat(s.price) || 0) / d) * 60; };

function Services() {
  const [services, setServices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('grid'); // grid, table, categories, pricelist
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('active'); // all, active, inactive, online
  const [sortBy, setSortBy] = useState('name');
  const [selectedIds, setSelectedIds] = useState([]);
  const [expandedCats, setExpandedCats] = useState({});

  const emptyForm = {
    name: '', description: '', duration: '60', price: '', category: '',
    is_active: true, allow_online_booking: true,
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [svcRes, statsRes] = await Promise.all([
        axios.get('/api/services'),
        axios.get('/api/services/stats').catch(() => ({ data: {} })),
      ]);
      if (svcRes.data?.success) setServices(svcRes.data.services || []);
      if (statsRes.data?.stats) setStats(statsRes.data.stats);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useAutoRefresh(loadData, 20000);

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (!form.name.trim() || !form.price) return alert('Name and price are required');
    try {
      var payload = {
        name: form.name, description: form.description, duration: parseInt(form.duration) || 60,
        price: parseFloat(form.price) || 0, category: form.category || null,
        is_active: form.is_active, allow_online_booking: form.allow_online_booking,
      };
      if (editing) await axios.put('/api/services/' + editing, payload);
      else await axios.post('/api/services', payload);
      setShowForm(false); setEditing(null); setForm(emptyForm); loadData();
    } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const handleEdit = (s) => {
    setForm({
      name: s.name || '', description: s.description || '', duration: String(s.duration || 60),
      price: String(s.price || ''), category: s.category || '',
      is_active: s.is_active !== false, allow_online_booking: s.allow_online_booking !== false,
    });
    setEditing(s.id); setShowForm(true);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm('Deactivate "' + name + '"?')) return;
    try { await axios.delete('/api/services/' + id); loadData(); }
    catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length || !window.confirm('Deactivate ' + selectedIds.length + ' services?')) return;
    try { await axios.post('/api/services/bulk-delete', { ids: selectedIds }); setSelectedIds([]); loadData(); }
    catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const handleReactivate = async (id) => {
    try { await axios.put('/api/services/' + id, { is_active: true }); loadData(); }
    catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const toggleSelect = (id) => setSelectedIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const filtered = useMemo(() => {
    var list = services.filter(s => {
      var ms = !search || s.name.toLowerCase().includes(search.toLowerCase()) ||
        (s.category || '').toLowerCase().includes(search.toLowerCase()) ||
        (s.description || '').toLowerCase().includes(search.toLowerCase());
      var mc = catFilter === 'All' || s.category === catFilter;
      var mst = statusFilter === 'all' ||
        (statusFilter === 'active' && s.is_active !== false) ||
        (statusFilter === 'inactive' && s.is_active === false) ||
        (statusFilter === 'online' && s.allow_online_booking !== false && s.is_active !== false);
      return ms && mc && mst;
    });
    list.sort((a, b) => {
      if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
      if (sortBy === 'price-high') return (parseFloat(b.price) || 0) - (parseFloat(a.price) || 0);
      if (sortBy === 'price-low') return (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0);
      if (sortBy === 'duration') return (a.duration || 0) - (b.duration || 0);
      if (sortBy === 'revenue') return revenuePerHour(b) - revenuePerHour(a);
      if (sortBy === 'category') return (a.category || 'zzz').localeCompare(b.category || 'zzz');
      return 0;
    });
    return list;
  }, [services, search, catFilter, statusFilter, sortBy]);

  const categories = useMemo(() => ['All', ...Array.from(new Set(services.map(s => s.category).filter(Boolean))).sort()], [services]);
  const grouped = useMemo(() => {
    var g = {};
    filtered.forEach(s => { var c = s.category || 'Uncategorized'; if (!g[c]) g[c] = []; g[c].push(s); });
    return g;
  }, [filtered]);

  var st = stats || {};
  var totalRevPerHour = filtered.length > 0 ? (filtered.reduce((s, svc) => s + revenuePerHour(svc), 0) / filtered.length) : 0;

  // ── Styles ──
  const S = {
    input: { width: '100%', padding: '12px 16px', background: '#1a1c2e', border: '1px solid #1e1e35', borderRadius: '10px', color: '#e2e2e8', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
    label: { fontSize: '13px', fontWeight: '600', color: '#888', marginBottom: '6px', display: 'block' },
    card: { background: '#12121f', border: '1px solid #1e1e35', borderRadius: '16px', padding: '20px' },
    pill: (active) => ({ padding: '7px 14px', borderRadius: '8px', border: 'none', fontSize: '12px', fontWeight: '600', cursor: 'pointer', background: active ? '#ff6b2b' : 'transparent', color: active ? '#fff' : '#888' }),
    viewBtn: (active) => ({ padding: '8px 12px', borderRadius: '8px', border: active ? '1px solid #ff6b2b' : '1px solid #1e1e35', background: active ? '#ff6b2b15' : 'transparent', color: active ? '#ff6b2b' : '#888', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }),
    badge: (color) => ({ display: 'inline-block', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', background: color + '20', color: color }),
  };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', color: '#888' }}><p>Loading services...</p></div>;

  return (
    <div style={{ maxWidth: '100%' }}>
      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '26px', fontWeight: '800', color: '#fff', margin: '0 0 6px' }}>Services</h1>
          <p style={{ fontSize: '14px', color: '#888', margin: 0 }}>{services.filter(s => s.is_active !== false).length} active · {categories.length - 1} categories</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {selectedIds.length > 0 && (
            <button onClick={handleBulkDelete} style={{ padding: '10px 16px', background: '#d63031', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
              🗑️ Deactivate {selectedIds.length}
            </button>
          )}
          <button onClick={() => { import('./lib/exportCSV').then(m => m.exportCSV('services')).catch(() => {}); }} style={{ padding: '10px 16px', background: '#1e1e35', border: '1px solid #2a2a45', borderRadius: '10px', color: '#aaa', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
            📥 Export
          </button>
          <button onClick={() => { setShowForm(true); setEditing(null); setForm(emptyForm); }} style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
            + Add Service
          </button>
        </div>
      </div>

      {/* ── Stats ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '20px' }}>
        {[
          { label: 'Total', value: st.total || services.length, color: '#ff6b2b' },
          { label: 'Active', value: st.active || services.filter(s => s.is_active !== false).length, color: '#00b894' },
          { label: 'Online Bookable', value: st.online || services.filter(s => s.allow_online_booking !== false && s.is_active !== false).length, color: '#0984e3' },
          { label: 'Avg Price', value: fmtPrice(st.avgPrice || 0), color: '#00b894' },
          { label: 'Price Range', value: fmtPrice(st.minPrice || 0) + '–' + fmtPrice(st.maxPrice || 0), color: '#888', small: true },
          { label: 'Avg $/Hour', value: fmtPrice(totalRevPerHour), color: '#6c5ce7' },
        ].map((s, i) => (
          <div key={i} style={{ ...S.card, padding: '16px' }}>
            <p style={{ fontSize: '10px', color: '#666', fontWeight: '700', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</p>
            <p style={{ fontSize: s.small ? '16px' : '22px', fontWeight: '800', color: s.color, margin: 0 }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* ── View Switcher + Filters ── */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '4px', background: '#1a1c2e', borderRadius: '10px', padding: '4px' }}>
          {[
            { key: 'grid', icon: '▦', label: 'Grid' },
            { key: 'table', icon: '☰', label: 'Table' },
            { key: 'categories', icon: '📁', label: 'Categories' },
            { key: 'pricelist', icon: '💲', label: 'Price List' },
          ].map(v => (
            <button key={v.key} onClick={() => setView(v.key)} style={S.viewBtn(view === v.key)}>{v.icon} {v.label}</button>
          ))}
        </div>
        <input placeholder="Search services..." value={search}
          onChange={e => setSearch(e.target.value)} style={{ ...S.input, maxWidth: '240px', flex: '1 1 180px' }} />
        {view !== 'pricelist' && (
          <div style={{ display: 'flex', gap: '3px', background: '#1a1c2e', borderRadius: '10px', padding: '4px' }}>
            {[
              { key: 'active', label: 'Active' }, { key: 'all', label: 'All' },
              { key: 'online', label: '🌐 Online' }, { key: 'inactive', label: 'Inactive' },
            ].map(f => <button key={f.key} onClick={() => setStatusFilter(f.key)} style={S.pill(statusFilter === f.key)}>{f.label}</button>)}
          </div>
        )}
        {categories.length > 2 && view !== 'categories' && (
          <div style={{ display: 'flex', gap: '3px', background: '#1a1c2e', borderRadius: '10px', padding: '4px', flexWrap: 'wrap' }}>
            {categories.map(c => <button key={c} onClick={() => setCatFilter(c)} style={S.pill(catFilter === c)}>{c}</button>)}
          </div>
        )}
        {(view === 'table' || view === 'grid') && (
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ ...S.input, maxWidth: '180px', cursor: 'pointer' }}>
            <option value="name">Sort: Name</option>
            <option value="price-high">Sort: Price ↓</option>
            <option value="price-low">Sort: Price ↑</option>
            <option value="duration">Sort: Duration ↑</option>
            <option value="revenue">Sort: $/Hour ↓</option>
            <option value="category">Sort: Category</option>
          </select>
        )}
      </div>

      {/* ════════════════════════════════════════ */}
      {/* ── GRID VIEW ── */}
      {/* ════════════════════════════════════════ */}
      {view === 'grid' && (
        filtered.length === 0 ? <Empty services={services} search={search} /> : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '14px' }}>
            {filtered.map(s => {
              var rph = revenuePerHour(s);
              var inactive = s.is_active === false;
              return (
                <div key={s.id} style={{ ...S.card, opacity: inactive ? 0.5 : 1, display: 'flex', flexDirection: 'column', transition: 'border-color 0.2s', cursor: 'default' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#ff6b2b40'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = '#1e1e35'}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ color: '#fff', fontSize: '15px', fontWeight: '700', margin: '0 0 4px' }}>{s.name}</h3>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {s.category && <span style={S.badge('#888')}>{s.category}</span>}
                        {inactive && <span style={S.badge('#d63031')}>INACTIVE</span>}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: '#00b894', fontWeight: '800', fontSize: '20px' }}>{fmtPrice(s.price)}</div>
                    </div>
                  </div>
                  {s.description && <p style={{ color: '#666', fontSize: '12px', margin: '0 0 10px', lineHeight: '1.4' }}>{s.description}</p>}
                  <div style={{ display: 'flex', gap: '12px', fontSize: '12px', marginBottom: '10px', flexWrap: 'wrap' }}>
                    <span style={{ color: '#aaa' }}>⏱ {fmtDur(s.duration)}</span>
                    <span style={{ color: '#6c5ce7', fontWeight: '600' }}>{fmtPrice(rph)}/hr</span>
                    {s.allow_online_booking !== false && s.is_active !== false && <span style={{ color: '#0984e3' }}>🌐 Online</span>}
                  </div>
                  <div style={{ display: 'flex', gap: '6px', marginTop: 'auto', paddingTop: '12px', borderTop: '1px solid #1e1e35' }}>
                    <button onClick={() => handleEdit(s)} style={{ flex: 1, padding: '8px', background: '#1e1e35', border: 'none', borderRadius: '8px', color: '#aaa', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>Edit</button>
                    {inactive ? (
                      <button onClick={() => handleReactivate(s.id)} style={{ flex: 1, padding: '8px', background: '#00b89420', border: 'none', borderRadius: '8px', color: '#00b894', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>Reactivate</button>
                    ) : (
                      <button onClick={() => handleDelete(s.id, s.name)} style={{ padding: '8px 14px', background: '#d6303110', border: 'none', borderRadius: '8px', color: '#d63031', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>✕</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ════════════════════════════════════════ */}
      {/* ── TABLE VIEW ── */}
      {/* ════════════════════════════════════════ */}
      {view === 'table' && (
        filtered.length === 0 ? <Empty services={services} search={search} /> : (
          <div style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #1e1e35' }}>
                    <th style={{ padding: '12px 10px', textAlign: 'left', width: '36px' }}>
                      <input type="checkbox" checked={selectedIds.length === filtered.length && filtered.length > 0}
                        onChange={() => setSelectedIds(selectedIds.length === filtered.length ? [] : filtered.map(s => s.id))}
                        style={{ width: '15px', height: '15px', accentColor: '#ff6b2b', cursor: 'pointer' }} />
                    </th>
                    {['Service', 'Category', 'Duration', 'Price', '$/Hour', 'Status', 'Online', ''].map(h => (
                      <th key={h} style={{ padding: '12px 10px', textAlign: 'left', fontSize: '10px', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(s => {
                    var rph = revenuePerHour(s);
                    var inactive = s.is_active === false;
                    return (
                      <tr key={s.id} style={{ borderBottom: '1px solid #1e1e3515', opacity: inactive ? 0.45 : 1 }}>
                        <td style={{ padding: '10px' }}>
                          <input type="checkbox" checked={selectedIds.includes(s.id)} onChange={() => toggleSelect(s.id)}
                            style={{ width: '15px', height: '15px', accentColor: '#ff6b2b', cursor: 'pointer' }} />
                        </td>
                        <td style={{ padding: '10px' }}>
                          <span style={{ color: '#fff', fontWeight: '600', fontSize: '13px' }}>{s.name}</span>
                          {s.description && <p style={{ color: '#555', fontSize: '11px', margin: '2px 0 0', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.description}</p>}
                        </td>
                        <td style={{ padding: '10px' }}>{s.category ? <span style={S.badge('#888')}>{s.category}</span> : <span style={{ color: '#444' }}>—</span>}</td>
                        <td style={{ padding: '10px', color: '#aaa', fontSize: '13px' }}>{fmtDur(s.duration)}</td>
                        <td style={{ padding: '10px', color: '#00b894', fontWeight: '700', fontSize: '14px' }}>{fmtPrice(s.price)}</td>
                        <td style={{ padding: '10px' }}>
                          <span style={{ color: rph >= 100 ? '#00b894' : rph >= 50 ? '#0984e3' : '#ffab40', fontWeight: '700', fontSize: '12px' }}>{fmtPrice(rph)}</span>
                        </td>
                        <td style={{ padding: '10px' }}>
                          {inactive ? <span style={S.badge('#d63031')}>Inactive</span> : <span style={S.badge('#00b894')}>Active</span>}
                        </td>
                        <td style={{ padding: '10px' }}>
                          {s.allow_online_booking !== false && !inactive ? <span style={{ color: '#0984e3', fontSize: '12px' }}>🌐 Yes</span> : <span style={{ color: '#444', fontSize: '12px' }}>—</span>}
                        </td>
                        <td style={{ padding: '10px' }}>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button onClick={() => handleEdit(s)} style={{ padding: '5px 10px', background: '#1e1e35', border: 'none', borderRadius: '6px', color: '#aaa', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>Edit</button>
                            {inactive ? (
                              <button onClick={() => handleReactivate(s.id)} style={{ padding: '5px 10px', background: '#00b89415', border: 'none', borderRadius: '6px', color: '#00b894', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>Restore</button>
                            ) : (
                              <button onClick={() => handleDelete(s.id, s.name)} style={{ padding: '5px 10px', background: '#d6303110', border: 'none', borderRadius: '6px', color: '#d63031', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>Del</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ padding: '10px 14px', borderTop: '1px solid #1e1e35', display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666' }}>
              <span>Showing {filtered.length} of {services.length}</span>
              <span style={{ color: '#6c5ce7' }}>Avg $/hr: {fmtPrice(totalRevPerHour)}</span>
            </div>
          </div>
        )
      )}

      {/* ════════════════════════════════════════ */}
      {/* ── CATEGORIES VIEW ── */}
      {/* ════════════════════════════════════════ */}
      {view === 'categories' && (
        Object.keys(grouped).length === 0 ? <Empty services={services} search={search} /> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {Object.entries(grouped).sort((a, b) => a[0].localeCompare(b[0])).map(([cat, items]) => {
              var isExpanded = expandedCats[cat] !== false;
              var active = items.filter(s => s.is_active !== false);
              var catAvgPrice = active.length > 0 ? active.reduce((s, svc) => s + (parseFloat(svc.price) || 0), 0) / active.length : 0;
              var catMinPrice = active.length > 0 ? Math.min(...active.map(s => parseFloat(s.price) || 0)) : 0;
              var catMaxPrice = active.length > 0 ? Math.max(...active.map(s => parseFloat(s.price) || 0)) : 0;
              return (
                <div key={cat} style={S.card}>
                  <div onClick={() => setExpandedCats(prev => ({ ...prev, [cat]: !isExpanded }))}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', padding: '4px 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '18px', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                      <h3 style={{ color: '#fff', fontSize: '16px', fontWeight: '700', margin: 0 }}>{cat}</h3>
                      <span style={{ color: '#666', fontSize: '13px' }}>{active.length} service{active.length !== 1 ? 's' : ''}</span>
                      {items.length !== active.length && <span style={S.badge('#666')}>{items.length - active.length} inactive</span>}
                    </div>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                      <span style={{ color: '#888', fontSize: '12px' }}>{fmtPrice(catMinPrice)}–{fmtPrice(catMaxPrice)}</span>
                      <span style={{ color: '#00b894', fontWeight: '700', fontSize: '14px' }}>avg {fmtPrice(catAvgPrice)}</span>
                    </div>
                  </div>
                  {isExpanded && (
                    <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {items.sort((a, b) => (parseFloat(b.price) || 0) - (parseFloat(a.price) || 0)).map(s => {
                        var rph = revenuePerHour(s); var inactive = s.is_active === false;
                        return (
                          <div key={s.id} style={{ background: '#1a1c2e', border: '1px solid #1e1e35', borderRadius: '12px', padding: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: inactive ? 0.45 : 1 }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '2px' }}>
                                <span style={{ color: '#fff', fontWeight: '600', fontSize: '13px' }}>{s.name}</span>
                                {inactive && <span style={S.badge('#d63031')}>OFF</span>}
                                {s.allow_online_booking !== false && !inactive && <span style={{ color: '#0984e3', fontSize: '10px' }}>🌐</span>}
                              </div>
                              <div style={{ display: 'flex', gap: '14px', fontSize: '12px' }}>
                                <span style={{ color: '#888' }}>{fmtDur(s.duration)}</span>
                                <span style={{ color: '#6c5ce7' }}>{fmtPrice(rph)}/hr</span>
                                {s.description && <span style={{ color: '#555', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.description}</span>}
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <span style={{ color: '#00b894', fontWeight: '800', fontSize: '16px' }}>{fmtPrice(s.price)}</span>
                              <button onClick={() => handleEdit(s)} style={{ padding: '5px 10px', background: '#1e1e35', border: 'none', borderRadius: '6px', color: '#aaa', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>Edit</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ════════════════════════════════════════ */}
      {/* ── PRICE LIST VIEW ── */}
      {/* ════════════════════════════════════════ */}
      {view === 'pricelist' && (
        <div style={{ ...S.card, maxWidth: '700px' }}>
          <div style={{ textAlign: 'center', marginBottom: '24px', paddingBottom: '20px', borderBottom: '2px solid #1e1e35' }}>
            <h2 style={{ color: '#fff', fontSize: '22px', fontWeight: '800', margin: '0 0 4px' }}>Service Menu</h2>
            <p style={{ color: '#888', fontSize: '13px', margin: 0 }}>{services.filter(s => s.is_active !== false).length} services available</p>
          </div>
          {Object.entries(
            services.filter(s => s.is_active !== false).reduce((g, s) => { var c = s.category || 'Services'; if (!g[c]) g[c] = []; g[c].push(s); return g; }, {})
          ).sort((a, b) => a[0].localeCompare(b[0])).map(([cat, items]) => (
            <div key={cat} style={{ marginBottom: '24px' }}>
              <h3 style={{ color: '#ff6b2b', fontSize: '14px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 12px', paddingBottom: '8px', borderBottom: '1px solid #1e1e3550' }}>
                {cat}
              </h3>
              {items.sort((a, b) => (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0)).map(s => (
                <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '8px 0', borderBottom: '1px dotted #1e1e3540' }}>
                  <div style={{ flex: 1 }}>
                    <span style={{ color: '#fff', fontWeight: '600', fontSize: '14px' }}>{s.name}</span>
                    {s.description && <span style={{ color: '#666', fontSize: '12px', marginLeft: '8px' }}>— {s.description}</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', whiteSpace: 'nowrap' }}>
                    <span style={{ color: '#888', fontSize: '12px' }}>{fmtDur(s.duration)}</span>
                    <span style={{ color: '#00b894', fontWeight: '800', fontSize: '16px' }}>{fmtPrice(s.price)}</span>
                  </div>
                </div>
              ))}
            </div>
          ))}
          <div style={{ textAlign: 'center', paddingTop: '16px', borderTop: '2px solid #1e1e35' }}>
            <p style={{ color: '#555', fontSize: '11px' }}>Prices subject to change · Book online for fastest service</p>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════ */}
      {/* ── ADD / EDIT SERVICE MODAL ── */}
      {/* ════════════════════════════════════════ */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: '#000000aa', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px' }}
          onClick={() => { setShowForm(false); setEditing(null); }}>
          <div style={{ ...S.card, maxWidth: '520px', width: '100%', maxHeight: '85vh', overflowY: 'auto', padding: '32px' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: '#fff', fontSize: '18px', fontWeight: '700', marginBottom: '24px' }}>{editing ? 'Edit Service' : 'New Service'}</h3>
            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={S.label}>Service Name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Oil Change, Deep Tissue Massage" style={S.input} autoFocus />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={S.label}>Price ($) *</label>
                  <input type="number" step="0.01" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} placeholder="0.00" style={S.input} />
                </div>
                <div>
                  <label style={S.label}>Duration</label>
                  <select value={form.duration} onChange={e => setForm({ ...form, duration: e.target.value })} style={{ ...S.input, cursor: 'pointer' }}>
                    {DURATIONS.map(m => <option key={m} value={m}>{fmtDur(m)}</option>)}
                  </select>
                </div>
              </div>
              {form.price && form.duration && (
                <div style={{ display: 'flex', gap: '16px', padding: '10px 14px', background: '#1a1c2e', borderRadius: '8px', fontSize: '12px' }}>
                  <span style={{ color: '#888' }}>Revenue/hour: <strong style={{ color: '#6c5ce7' }}>{fmtPrice(((parseFloat(form.price) || 0) / (parseInt(form.duration) || 60)) * 60)}</strong></span>
                  <span style={{ color: '#888' }}>Per min: <strong style={{ color: '#888' }}>{fmtPrice((parseFloat(form.price) || 0) / (parseInt(form.duration) || 60))}</strong></span>
                </div>
              )}
              <div>
                <label style={S.label}>Category</label>
                <input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                  placeholder="e.g. Maintenance, Repair, Body" list="cat-suggestions" style={S.input} />
                <datalist id="cat-suggestions">
                  {categories.filter(c => c !== 'All').map(c => <option key={c} value={c} />)}
                </datalist>
              </div>
              <div>
                <label style={S.label}>Description</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="What's included in this service..." rows="2" style={{ ...S.input, resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', gap: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#aaa', fontSize: '13px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} style={{ width: '16px', height: '16px', accentColor: '#ff6b2b' }} />
                  Active
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#aaa', fontSize: '13px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.allow_online_booking} onChange={e => setForm({ ...form, allow_online_booking: e.target.checked })} style={{ width: '16px', height: '16px', accentColor: '#ff6b2b' }} />
                  Allow Online Booking
                </label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button onClick={() => { setShowForm(false); setEditing(null); }} style={{ flex: 1, padding: '14px', background: '#1e1e35', border: 'none', borderRadius: '10px', color: '#aaa', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleSave} style={{ flex: 1, padding: '14px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: '700', cursor: 'pointer' }}>{editing ? 'Save Changes' : 'Add Service'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Empty({ services, search }) {
  return (
    <div style={{ textAlign: 'center', padding: '64px 24px', background: '#12121f', borderRadius: '20px', border: '1px solid #1e1e35' }}>
      <p style={{ fontSize: '40px', marginBottom: '12px' }}>🛠️</p>
      <p style={{ color: '#fff', fontSize: '18px', fontWeight: '700', marginBottom: '4px' }}>
        {services.length === 0 ? 'No services yet' : 'No matching services'}
      </p>
      <p style={{ color: '#888', fontSize: '14px' }}>
        {services.length === 0 ? 'Add your first service to get started' : 'Try adjusting your search or filters'}
      </p>
    </div>
  );
}

export default Services;
